import React, { forwardRef, ButtonHTMLAttributes, ReactNode } from 'react'
import styled, { css } from 'styled-components'

type Variant = 'accent' | 'ghost' | 'danger'

export interface AccentButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  iconRight?: ReactNode
  fullWidth?: boolean
}

export const AccentButton = forwardRef<HTMLButtonElement, AccentButtonProps>(
  ({ children, variant = 'accent', iconRight, fullWidth = true, ...rest }, ref) => (
    <StyledButton ref={ref} $variant={variant} $fullWidth={fullWidth} {...rest}>
      <span>{children}</span>
      {iconRight && <IconWrap>{iconRight}</IconWrap>}
    </StyledButton>
  ),
)
AccentButton.displayName = 'AccentButton'

const variantStyles: Record<Variant, ReturnType<typeof css>> = {
  accent: css`
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};
    border: 0;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      background: ${({ theme }) => theme.colors.accentDeep};
      box-shadow: ${({ theme }) => theme.shadows.submitHover};
    }
  `,
  ghost: css`
    background: transparent;
    color: ${({ theme }) => theme.colors.ink2};
    border: 1px solid ${({ theme }) => theme.colors.line};

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.line2};
      color: ${({ theme }) => theme.colors.ink};
    }
  `,
  danger: css`
    background: ${({ theme }) => theme.colors.danger};
    color: #fff;
    border: 0;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      filter: brightness(0.92);
    }
  `,
}

const StyledButton = styled.button<{ $variant: Variant; $fullWidth: boolean }>`
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  border-radius: ${({ theme }) => theme.radii.md};
  padding: 0.875rem 1rem;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  transition:
    transform ${({ theme }) => theme.motion.fast},
    box-shadow ${({ theme }) => theme.motion.fast},
    background ${({ theme }) => theme.motion.fast};

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  ${({ $variant }) => variantStyles[$variant]}

  &:hover:not(:disabled) svg {
    transform: translateX(-3px);
  }
`

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  svg {
    transition: transform ${({ theme }) => theme.motion.fast};
  }
`

export default AccentButton
